import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/auth';
import { useMessaging } from '../contexts/MessagingContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { CreateInviteForm } from '../components/CreateInviteForm';
import { Modal } from '../components/Modal';
import { FamilyRole } from '@kinapp/shared';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Dependent {
  id: string;
  name: string;
  dateOfBirth?: string;
  notes?: string;
  isActive: boolean;
  emergencyProfile?: { gpName?: string };
}

interface FamilyMember {
  id: string;
  role: FamilyRole;
  user: { id: string; name: string; email: string; avatarUrl?: string };
}

const ROLE_STYLE: Record<string, string> = {
  OWNER: 'bg-primary-100 text-primary-800',
  MEMBER: 'bg-gray-100 text-gray-700',
  VIEWER: 'bg-gray-50 text-gray-500',
};

// ─── Pro (Care Home) Types ────────────────────────────────────────────────────

interface StaffMember {
  id: string;
  email: string;
  name?: string;
  isAdmin: boolean;
  isActive: boolean;
  claimedAt?: string;
  user?: { id: string; name: string; email: string };
}

interface CareHomeDependent {
  id: string;
  name: string;
  dateOfBirth?: string;
}

interface Schedule {
  id: string;
  staffId: string;
  dependentId: string;
  shiftDate: string;
  startTime: string;
  endTime: string;
  isPrimary: boolean;
  staff: { id: string; name?: string; email: string; user?: { name: string } };
  dependent: { id: string; name: string };
}

function ageLabel(dateOfBirth?: string): string {
  if (!dateOfBirth) return '';
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return `Age ${age}`;
}

function avatarInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}

// ─── Filter/sort helpers ──────────────────────────────────────────────────────

function getItemName(item: any): string {
  return item?.user?.name ?? item?.name ?? item?.email ?? '';
}

function applyFilter<T>(
  items: T[],
  query: string,
  order: 'name_asc' | 'name_desc' | 'recent',
): T[] {
  let result = [...items];
  if (query.trim()) {
    const q = query.toLowerCase();
    result = result.filter((i) => getItemName(i).toLowerCase().includes(q));
  }
  if (order === 'name_asc') result.sort((a, b) => getItemName(a).localeCompare(getItemName(b)));
  if (order === 'name_desc') result.sort((a, b) => getItemName(b).localeCompare(getItemName(a)));
  return result;
}

// ─── Add Dependent inline form ────────────────────────────────────────────────

function AddDependentForm({
  familyId,
  onSuccess,
  onCancel,
}: {
  familyId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.createDependent(familyId, { name: name.trim(), dateOfBirth: dob || undefined });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to add dependent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-4 border border-dashed border-gray-300 rounded-xl bg-gray-50">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Add dependent</h4>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" required className="input w-full" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date of birth</label>
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="input w-full" />
        </div>
        <div className="flex gap-2">
          <button type="submit" disabled={saving || !name.trim()} className="btn btn-primary flex-1 text-sm">
            {saving ? 'Adding…' : 'Add'}
          </button>
          <button type="button" onClick={onCancel} className="btn flex-1 text-sm">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ─── Icon helpers ─────────────────────────────────────────────────────────────

function ListIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}

// ─── GearIcon ─────────────────────────────────────────────────────────────────

function GearIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// ─── PeoplePage ───────────────────────────────────────────────────────────────

export function PeoplePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openConversation } = useMessaging();
  const { activeWorkspace, isPro } = useWorkspace();

  // ── Family state ──────────────────────────────────────────────────────────
  const [familyId, setFamilyId] = useState('');
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [carerLoad, setCarerLoad] = useState<any[]>([]);

  // ── Pro (care home) state ─────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'residents' | 'staff' | 'schedules'>('residents');
  const [careHomeDependents, setCareHomeDependents] = useState<CareHomeDependent[]>([]);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [scheduleStaff, setScheduleStaff] = useState<StaffMember[]>([]);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterStaffId, setFilterStaffId] = useState('');
  const [filterDependentId, setFilterDependentId] = useState('');
  // Add staff modal
  const [isAddStaffOpen, setIsAddStaffOpen] = useState(false);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffIsAdmin, setNewStaffIsAdmin] = useState(false);
  const [addingStaff, setAddingStaff] = useState(false);
  // Add schedule modal
  const [isAddScheduleOpen, setIsAddScheduleOpen] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ staffId: '', dependentId: '', shiftDate: selectedDate, startTime: '09:00', endTime: '17:00', isPrimary: false });
  const [addingSchedule, setAddingSchedule] = useState(false);

  // ── Shared state ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [memberActionId, setMemberActionId] = useState<string | null>(null);
  const [dmLoading, setDmLoading] = useState<string | null>(null);
  const [showAddDep, setShowAddDep] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  // ── View / filter state ───────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<'list' | 'card'>(() =>
    (localStorage.getItem('peopleViewMode') as 'list' | 'card') ?? 'list'
  );
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'name_asc' | 'name_desc' | 'recent'>('name_asc');

  const toggleViewMode = (mode: 'list' | 'card') => {
    localStorage.setItem('peopleViewMode', mode);
    setViewMode(mode);
  };

  useEffect(() => {
    if (!activeWorkspace) return;
    (async () => {
      try {
        if (isPro) {
          const careHomeId = activeWorkspace.id;
          const [deps, staff] = await Promise.all([
            api.getCareHomeDependents(careHomeId),
            api.getStaff(careHomeId),
          ]);
          setCareHomeDependents(deps as CareHomeDependent[]);
          setStaffList(staff as StaffMember[]);
          const me = (staff as StaffMember[]).find((s) => s.user?.id === user?.id);
          setIsAdminUser(me?.isAdmin ?? false);
        } else {
          const families = await api.getFamilies();
          if (!families?.length) { navigate('/onboarding'); return; }
          const fid = families[0].id;
          setFamilyId(fid);
          const [deps, mems, load] = await Promise.all([
            api.getDependents(fid, true),
            api.getFamilyMembers(fid),
            api.getCarerLoad(fid),
          ]);
          setDependents(deps as Dependent[]);
          setMembers(mems as FamilyMember[]);
          setCarerLoad(load as any[]);
          const me = (mems as FamilyMember[]).find((m) => m.user.id === user?.id);
          setIsOwner(me?.role === 'OWNER');
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [activeWorkspace?.id, isPro, navigate, user?.id]);

  const reloadSchedules = async () => {
    if (!activeWorkspace) return;
    try {
      const params: any = {};
      if (selectedDate) params.date = selectedDate;
      if (filterStaffId) params.staffId = filterStaffId;
      if (filterDependentId) params.dependentId = filterDependentId;
      const [schedulesData, staffData] = await Promise.all([
        api.getSchedules(activeWorkspace.id, params),
        api.getStaff(activeWorkspace.id),
      ]);
      setSchedules(schedulesData as Schedule[]);
      setScheduleStaff(staffData as StaffMember[]);
    } catch { /* ignore */ }
  };

  const reload = async () => {
    if (!familyId) return;
    try {
      const [deps, mems] = await Promise.all([
        api.getDependents(familyId, true),
        api.getFamilyMembers(familyId),
      ]);
      setDependents(deps as Dependent[]);
      setMembers(mems as FamilyMember[]);
      const me = (mems as FamilyMember[]).find((m) => m.user.id === user?.id);
      setIsOwner(me?.role === 'OWNER');
    } catch {
      // ignore
    }
  };

  const handleRoleChange = async (memberId: string, role: 'OWNER' | 'MEMBER' | 'VIEWER') => {
    setMemberActionId(memberId);
    try {
      await api.updateMemberRole(familyId, memberId, role);
      await reload();
    } catch (err: any) {
      alert(err.message || 'Failed to update role');
    } finally {
      setMemberActionId(null);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string, isMe: boolean) => {
    const msg = isMe
      ? 'Leave this family? You will lose access.'
      : `Remove ${memberName} from the family?`;
    if (!confirm(msg)) return;
    setMemberActionId(memberId);
    try {
      await api.removeFamilyMember(familyId, memberId);
      if (isMe) { navigate('/onboarding'); return; }
      await reload();
    } catch (err: any) {
      alert(err.message || 'Failed to remove member');
    } finally {
      setMemberActionId(null);
    }
  };

  const handleDirectMessage = async (memberId: string, memberUserId: string) => {
    if (!familyId) return;
    setDmLoading(memberId);
    try {
      await openConversation({ familyId, targetUserId: memberUserId });
      navigate('/messages');
    } catch {
      // ignore
    } finally {
      setDmLoading(null);
    }
  };

  // ── Pro (care home) handlers ─────────────────────────────────────────────

  useEffect(() => {
    if (isPro && activeTab === 'schedules') {
      reloadSchedules();
    }
  }, [isPro, activeTab, selectedDate, filterStaffId, filterDependentId]);

  const handleAddStaff = async (e: FormEvent) => {
    e.preventDefault();
    if (!activeWorkspace) return;
    setAddingStaff(true);
    setError('');
    try {
      const created = await api.createStaff(activeWorkspace.id, {
        email: newStaffEmail,
        name: newStaffName || undefined,
        isAdmin: newStaffIsAdmin,
      });
      setStaffList((prev) => [...prev, created as StaffMember]);
      setIsAddStaffOpen(false);
      setNewStaffEmail('');
      setNewStaffName('');
      setNewStaffIsAdmin(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add staff');
    } finally {
      setAddingStaff(false);
    }
  };

  const handleToggleAdmin = async (staffId: string, currentIsAdmin: boolean) => {
    if (!activeWorkspace) return;
    try {
      await api.updateStaff(activeWorkspace.id, staffId, { isAdmin: !currentIsAdmin });
      setStaffList((prev) => prev.map((s) => s.id === staffId ? { ...s, isAdmin: !currentIsAdmin } : s));
    } catch (err: any) {
      setError(err.message || 'Failed to update staff');
    }
  };

  const handleToggleActive = async (staffId: string, currentIsActive: boolean) => {
    if (!activeWorkspace) return;
    try {
      await api.updateStaff(activeWorkspace.id, staffId, { isActive: !currentIsActive });
      setStaffList((prev) => prev.map((s) => s.id === staffId ? { ...s, isActive: !currentIsActive } : s));
    } catch (err: any) {
      setError(err.message || 'Failed to update staff');
    }
  };

  const handleDeleteStaff = async (staffId: string) => {
    if (!activeWorkspace) return;
    if (!confirm('Remove this staff member?')) return;
    try {
      await api.deleteStaff(activeWorkspace.id, staffId);
      setStaffList((prev) => prev.filter((s) => s.id !== staffId));
    } catch (err: any) {
      setError(err.message || 'Failed to remove staff');
    }
  };

  const handleAddSchedule = async (e: FormEvent) => {
    e.preventDefault();
    if (!activeWorkspace) return;
    setAddingSchedule(true);
    setError('');
    try {
      const created = await api.createSchedule(activeWorkspace.id, newSchedule);
      setSchedules((prev) => [...prev, created as Schedule]);
      setIsAddScheduleOpen(false);
      setNewSchedule({ staffId: '', dependentId: '', shiftDate: selectedDate, startTime: '09:00', endTime: '17:00', isPrimary: false });
    } catch (err: any) {
      setError(err.message || 'Failed to create schedule');
    } finally {
      setAddingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!activeWorkspace) return;
    if (!confirm('Delete this schedule?')) return;
    try {
      await api.deleteSchedule(activeWorkspace.id, scheduleId);
      setSchedules((prev) => prev.filter((s) => s.id !== scheduleId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete schedule');
    }
  };

  const handleTogglePrimary = async (schedule: Schedule) => {
    if (!activeWorkspace) return;
    try {
      await api.updateSchedule(activeWorkspace.id, schedule.id, { isPrimary: !schedule.isPrimary });
      setSchedules((prev) => prev.map((s) => s.id === schedule.id ? { ...s, isPrimary: !s.isPrimary } : s));
    } catch (err: any) {
      setError(err.message || 'Failed to update schedule');
    }
  };

  const getStaffDisplayName = (s: StaffMember) => s.user?.name || s.name || s.email;

  const schedulesByDependent = schedules.reduce((acc, schedule) => {
    const targetId = schedule.dependentId;
    if (!acc[targetId]) acc[targetId] = { dependent: schedule.dependent, schedules: [] };
    acc[targetId].schedules.push(schedule);
    return acc;
  }, {} as Record<string, { dependent: { id: string; name: string }; schedules: Schedule[] }>);

  // ── Family helpers ────────────────────────────────────────────────────────
  const activeDeps = dependents.filter((d) => d.isActive);
  const archivedDeps = dependents.filter((d) => !d.isActive);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  // ── Shared controls bar ───────────────────────────────────────────────────
  const controlsBar = (
    <div className="flex items-center gap-2 mb-4">
      <input
        type="text"
        placeholder="Search…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="input flex-1 min-w-0"
      />
      <select
        value={sortOrder}
        onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
        className="input w-auto shrink-0 text-sm"
      >
        <option value="name_asc">Name A–Z</option>
        <option value="name_desc">Name Z–A</option>
        <option value="recent">Recently Added</option>
      </select>
      <div className="flex border border-gray-200 rounded-lg overflow-hidden shrink-0">
        <button
          onClick={() => toggleViewMode('list')}
          title="List view"
          className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600 bg-white'}`}
        >
          <ListIcon />
        </button>
        <button
          onClick={() => toggleViewMode('card')}
          title="Card view"
          className={`p-2 transition-colors border-l border-gray-200 ${viewMode === 'card' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600 bg-white'}`}
        >
          <GridIcon />
        </button>
      </div>
    </div>
  );

  // ── Pro Dashboard (Care Home) ─────────────────────────────────────────────
  if (isPro) {
    const TABS = [
      { key: 'residents', label: 'Residents' },
      { key: 'staff', label: 'Staff' },
      ...(isAdminUser ? [{ key: 'schedules', label: 'Schedules' }] : []),
    ] as const;

    const filteredResidents = applyFilter(careHomeDependents, search, sortOrder);
    const filteredStaff = applyFilter(staffList, search, sortOrder);

    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key as typeof activeTab); setSearch(''); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Residents tab */}
        {activeTab === 'residents' && (
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Residents</h2>
            {controlsBar}
            {filteredResidents.length > 0 ? (
              viewMode === 'card' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredResidents.map((dep) => (
                    <div key={dep.id} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center text-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 font-bold text-lg flex items-center justify-center">
                        {avatarInitial(dep.name)}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{dep.name}</p>
                        {dep.dateOfBirth && (
                          <p className="text-xs text-gray-500 mt-0.5">{ageLabel(dep.dateOfBirth)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredResidents.map((dep) => (
                    <div key={dep.id} className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="font-medium text-gray-900">{dep.name}</p>
                      {dep.dateOfBirth && (
                        <p className="text-xs text-gray-500 mt-0.5">{ageLabel(dep.dateOfBirth)}</p>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="text-sm text-gray-400">
                {search ? 'No residents match your search.' : 'No residents added yet.'}
              </p>
            )}
          </section>
        )}

        {/* Staff tab */}
        {activeTab === 'staff' && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Staff</h2>
              {isAdminUser && (
                <button onClick={() => setIsAddStaffOpen(true)} className="btn btn-primary text-sm">
                  Add Staff
                </button>
              )}
            </div>
            {controlsBar}
            {viewMode === 'card' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredStaff.map((member) => (
                  <div
                    key={member.id}
                    className={`bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3 ${!member.isActive ? 'opacity-60' : ''}`}
                  >
                    <div className="flex flex-col items-center text-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 font-bold text-lg flex items-center justify-center">
                        {avatarInitial(getStaffDisplayName(member))}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{getStaffDisplayName(member)}</p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[110px]">{member.email}</p>
                        {!member.claimedAt && (
                          <p className="text-xs text-yellow-600 mt-0.5">Pending</p>
                        )}
                        <div className="flex flex-wrap gap-1 justify-center mt-1.5">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${member.isAdmin ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-700'}`}>
                            {member.isAdmin ? 'Admin' : 'Staff'}
                          </span>
                          {!member.isActive && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                              Disabled
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {isAdminUser && (
                      <div className="border-t border-gray-100 pt-2 flex flex-wrap gap-1.5 justify-center">
                        <button
                          onClick={() => handleToggleAdmin(member.id, member.isAdmin)}
                          className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          {member.isAdmin ? 'Remove Admin' : 'Make Admin'}
                        </button>
                        <button
                          onClick={() => handleToggleActive(member.id, member.isActive)}
                          className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          {member.isActive ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleDeleteStaff(member.id)}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {filteredStaff.length === 0 && (
                  <p className="text-sm text-gray-400 col-span-full">
                    {search ? 'No staff match your search.' : 'No staff yet.'}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name / Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      {isAdminUser && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredStaff.map((member) => (
                      <tr key={member.id} className={!member.isActive ? 'bg-gray-50 opacity-60' : ''}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm flex items-center justify-center shrink-0">
                              {avatarInitial(getStaffDisplayName(member))}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{member.user?.name || member.name || 'Unclaimed'}</div>
                              <div className="text-xs text-gray-500">{member.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${member.claimedAt ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {member.claimedAt ? 'Active' : 'Pending'}
                          </span>
                          {!member.isActive && (
                            <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Disabled
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isAdminUser ? (
                            <button
                              onClick={() => handleToggleAdmin(member.id, member.isAdmin)}
                              className={`text-sm ${member.isAdmin ? 'text-primary-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              {member.isAdmin ? 'Admin' : 'Staff'}
                            </button>
                          ) : (
                            <span className={`text-sm ${member.isAdmin ? 'text-primary-600 font-medium' : 'text-gray-500'}`}>
                              {member.isAdmin ? 'Admin' : 'Staff'}
                            </span>
                          )}
                        </td>
                        {isAdminUser && (
                          <td className="px-4 py-3 text-right text-sm space-x-3">
                            <button onClick={() => handleToggleActive(member.id, member.isActive)} className="text-gray-500 hover:text-gray-700">
                              {member.isActive ? 'Disable' : 'Enable'}
                            </button>
                            <button onClick={() => handleDeleteStaff(member.id)} className="text-red-500 hover:text-red-700">
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {filteredStaff.length === 0 && (
                      <tr>
                        <td colSpan={isAdminUser ? 4 : 3} className="px-4 py-8 text-center text-sm text-gray-400">
                          {search ? 'No staff match your search.' : 'No staff yet.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add Staff Modal */}
            <Modal isOpen={isAddStaffOpen} onClose={() => setIsAddStaffOpen(false)} title="Add Staff Member">
              <form onSubmit={handleAddStaff} className="space-y-4">
                <div>
                  <label className="label">Email *</label>
                  <input type="email" required className="input" value={newStaffEmail} onChange={(e) => setNewStaffEmail(e.target.value)} placeholder="staff@example.com" />
                </div>
                <div>
                  <label className="label">Display Name (optional)</label>
                  <input type="text" className="input" value={newStaffName} onChange={(e) => setNewStaffName(e.target.value)} placeholder="John Smith" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="isAdmin" className="rounded border-gray-300" checked={newStaffIsAdmin} onChange={(e) => setNewStaffIsAdmin(e.target.checked)} />
                  <label htmlFor="isAdmin" className="text-sm text-gray-700">Make administrator</label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setIsAddStaffOpen(false)} className="btn btn-secondary">Cancel</button>
                  <button type="submit" disabled={addingStaff} className="btn btn-primary disabled:opacity-50">{addingStaff ? 'Adding…' : 'Add Staff'}</button>
                </div>
              </form>
            </Modal>
          </section>
        )}

        {/* Schedules tab (admin only) */}
        {activeTab === 'schedules' && isAdminUser && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Schedules</h2>
              <button onClick={() => setIsAddScheduleOpen(true)} className="btn btn-primary text-sm">
                Add Schedule
              </button>
            </div>
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-4">
              <div>
                <label className="label text-xs">Date</label>
                <input type="date" className="input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              </div>
              <div>
                <label className="label text-xs">Staff</label>
                <select className="input" value={filterStaffId} onChange={(e) => setFilterStaffId(e.target.value)}>
                  <option value="">All Staff</option>
                  {scheduleStaff.map((s) => <option key={s.id} value={s.id}>{getStaffDisplayName(s)}</option>)}
                </select>
              </div>
              <div>
                <label className="label text-xs">Resident</label>
                <select className="input" value={filterDependentId} onChange={(e) => setFilterDependentId(e.target.value)}>
                  <option value="">All Residents</option>
                  {careHomeDependents.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                </select>
              </div>
            </div>
            {/* Schedule grid */}
            {Object.keys(schedulesByDependent).length > 0 ? (
              <div className="space-y-4">
                {Object.values(schedulesByDependent).map(({ dependent, schedules: depSchedules }) => (
                  <div key={dependent.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h3 className="font-semibold text-gray-900">{dependent.name}</h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {depSchedules.map((schedule) => (
                        <div key={schedule.id} className="px-4 py-3 flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{getStaffDisplayName(schedule.staff as StaffMember)}</div>
                            <div className="text-sm text-gray-500">{schedule.startTime} – {schedule.endTime}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            {schedule.isPrimary && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">Primary</span>
                            )}
                            <button onClick={() => handleTogglePrimary(schedule)} className="text-sm text-gray-500 hover:text-gray-700">
                              {schedule.isPrimary ? 'Remove Primary' : 'Set Primary'}
                            </button>
                            <button onClick={() => handleDeleteSchedule(schedule.id)} className="text-sm text-red-500 hover:text-red-700">Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">
                No schedules for this date
              </div>
            )}

            {/* Add Schedule Modal */}
            <Modal isOpen={isAddScheduleOpen} onClose={() => setIsAddScheduleOpen(false)} title="Add Schedule">
              <form onSubmit={handleAddSchedule} className="space-y-4">
                <div>
                  <label className="label">Staff Member *</label>
                  <select required className="input" value={newSchedule.staffId} onChange={(e) => setNewSchedule({ ...newSchedule, staffId: e.target.value })}>
                    <option value="">Select staff…</option>
                    {scheduleStaff.map((s) => <option key={s.id} value={s.id}>{getStaffDisplayName(s)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Resident *</label>
                  <select required className="input" value={newSchedule.dependentId} onChange={(e) => setNewSchedule({ ...newSchedule, dependentId: e.target.value })}>
                    <option value="">Select resident…</option>
                    {careHomeDependents.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Date *</label>
                  <input type="date" required className="input" value={newSchedule.shiftDate} onChange={(e) => setNewSchedule({ ...newSchedule, shiftDate: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Start *</label>
                    <input type="time" required className="input" value={newSchedule.startTime} onChange={(e) => setNewSchedule({ ...newSchedule, startTime: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">End *</label>
                    <input type="time" required className="input" value={newSchedule.endTime} onChange={(e) => setNewSchedule({ ...newSchedule, endTime: e.target.value })} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="isPrimary" className="rounded border-gray-300" checked={newSchedule.isPrimary} onChange={(e) => setNewSchedule({ ...newSchedule, isPrimary: e.target.checked })} />
                  <label htmlFor="isPrimary" className="text-sm text-gray-700">Primary carer for this shift</label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setIsAddScheduleOpen(false)} className="btn btn-secondary">Cancel</button>
                  <button type="submit" disabled={addingSchedule} className="btn btn-primary disabled:opacity-50">{addingSchedule ? 'Adding…' : 'Add Schedule'}</button>
                </div>
              </form>
            </Modal>
          </section>
        )}
      </div>
    );
  }

  // ── Family People Page ────────────────────────────────────────────────────
  const filteredActiveDeps = applyFilter(activeDeps, search, sortOrder);
  const filteredMembers = applyFilter(members, search, sortOrder);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">

      {/* ── Dependents ────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {activeDeps.length === 1 ? 'Dependent' : 'Dependents'}
        </h2>

        {controlsBar}

        {filteredActiveDeps.length > 0 && (
          viewMode === 'card' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
              {filteredActiveDeps.map((dep) => (
                <div key={dep.id} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
                  <div className="flex flex-col items-center text-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 font-bold text-lg flex items-center justify-center">
                      {avatarInitial(dep.name)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{dep.name}</p>
                      {dep.dateOfBirth && (
                        <p className="text-xs text-gray-500 mt-0.5">{ageLabel(dep.dateOfBirth)}</p>
                      )}
                      {dep.emergencyProfile?.gpName && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[110px]">GP: {dep.emergencyProfile.gpName}</p>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-2 flex justify-center">
                    <button
                      onClick={() => navigate('/settings/dependents/' + dep.id)}
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              {filteredActiveDeps.map((dep) => (
                <div
                  key={dep.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{dep.name}</p>
                    {dep.dateOfBirth && (
                      <p className="text-xs text-gray-500 mt-0.5">{ageLabel(dep.dateOfBirth)}</p>
                    )}
                    {dep.emergencyProfile?.gpName && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">GP: {dep.emergencyProfile.gpName}</p>
                    )}
                    {dep.notes && !dep.emergencyProfile?.gpName && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{dep.notes}</p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate('/settings/dependents/' + dep.id)}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label={`Edit ${dep.name}`}
                    title="Edit"
                  >
                    <GearIcon />
                  </button>
                </div>
              ))}
            </div>
          )
        )}

        {filteredActiveDeps.length === 0 && !showAddDep && (
          <p className="text-sm text-gray-400 mb-3">
            {search ? 'No dependents match your search.' : 'No dependents added yet.'}
          </p>
        )}

        {isOwner && !showAddDep && (
          <button
            onClick={() => setShowAddDep(true)}
            className="w-full py-2.5 rounded-xl border border-dashed border-gray-300 text-sm text-gray-500 hover:text-primary-700 hover:border-primary-300 hover:bg-primary-50/30 transition-colors"
          >
            + Add dependent
          </button>
        )}
        {showAddDep && (
          <AddDependentForm
            familyId={familyId}
            onSuccess={() => { setShowAddDep(false); reload(); }}
            onCancel={() => setShowAddDep(false)}
          />
        )}

        {archivedDeps.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
            >
              <span>{showArchived ? '▾' : '▸'}</span>
              {archivedDeps.length} archived
            </button>
            {showArchived && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {archivedDeps.map((dep) => (
                  <div
                    key={dep.id}
                    className="bg-gray-50 rounded-xl border border-gray-100 p-4 flex items-start justify-between gap-3 opacity-70"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-700 truncate">{dep.name}</p>
                      <p className="text-xs text-amber-600 mt-0.5">Archived</p>
                    </div>
                    <button
                      onClick={() => navigate('/settings/dependents/' + dep.id)}
                      className="shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      aria-label={`Edit ${dep.name}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Family members ─────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Family members</h2>

        {controlsBar}

        {viewMode === 'card' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredMembers.map((member) => {
              const isMe = member.user.id === user?.id;
              const isBusy = memberActionId === member.id;
              return (
                <div key={member.id} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
                  <div className="flex flex-col items-center text-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 font-bold text-lg flex items-center justify-center shrink-0 overflow-hidden">
                      {member.user.avatarUrl ? (
                        <img src={member.user.avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        avatarInitial(member.user.name)
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        {member.user.name}
                        {isMe && <span className="text-gray-400 font-normal"> (you)</span>}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[110px]">{member.user.email}</p>
                      <span className={`mt-1.5 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${ROLE_STYLE[member.role]}`}>
                        {member.role[0] + member.role.slice(1).toLowerCase()}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-2 flex flex-wrap gap-1.5 justify-center">
                    {!isMe && (
                      <button
                        onClick={() => handleDirectMessage(member.id, member.user.id)}
                        disabled={dmLoading === member.id}
                        className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        {dmLoading === member.id ? '…' : 'Message'}
                      </button>
                    )}
                    {isOwner && !isMe && (
                      <>
                        {(['OWNER', 'MEMBER', 'VIEWER'] as const).map((r) => (
                          <button
                            key={r}
                            onClick={() => handleRoleChange(member.id, r)}
                            disabled={isBusy || member.role === r}
                            className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                              member.role === r
                                ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium'
                                : 'border-gray-200 text-gray-500 hover:border-gray-400'
                            } disabled:opacity-50`}
                          >
                            {r[0] + r.slice(1).toLowerCase()}
                          </button>
                        ))}
                        <button
                          onClick={() => handleRemoveMember(member.id, member.user.name, false)}
                          disabled={isBusy}
                          className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50 transition-colors"
                        >
                          Remove
                        </button>
                      </>
                    )}
                    {isMe && !isOwner && (
                      <button
                        onClick={() => handleRemoveMember(member.id, member.user.name, true)}
                        disabled={isBusy}
                        className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50 transition-colors"
                      >
                        {isBusy ? 'Leaving…' : 'Leave'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredMembers.length === 0 && (
              <p className="text-sm text-gray-400 col-span-full">
                {search ? 'No members match your search.' : 'No members yet.'}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {filteredMembers.map((member) => {
              const isMe = member.user.id === user?.id;
              const isBusy = memberActionId === member.id;
              const canEdit = isOwner && !isMe;

              return (
                <div key={member.id} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm flex items-center justify-center shrink-0">
                      {member.user.avatarUrl ? (
                        <img src={member.user.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        avatarInitial(member.user.name)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {member.user.name}
                        {isMe && <span className="text-gray-400 font-normal"> (you)</span>}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{member.user.email}</p>
                    </div>
                    <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${ROLE_STYLE[member.role]}`}>
                      {member.role[0] + member.role.slice(1).toLowerCase()}
                    </span>
                    {!isMe && (
                      <button
                        onClick={() => handleDirectMessage(member.id, member.user.id)}
                        disabled={dmLoading === member.id}
                        className="shrink-0 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        {dmLoading === member.id ? '…' : 'Message'}
                      </button>
                    )}
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-1.5 mt-2.5 pl-12">
                      <span className="text-xs text-gray-400">Role:</span>
                      {(['OWNER', 'MEMBER', 'VIEWER'] as const).map((r) => (
                        <button
                          key={r}
                          onClick={() => handleRoleChange(member.id, r)}
                          disabled={isBusy || member.role === r}
                          className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                            member.role === r
                              ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium'
                              : 'border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
                          } disabled:opacity-50`}
                        >
                          {r[0] + r.slice(1).toLowerCase()}
                        </button>
                      ))}
                      <button
                        onClick={() => handleRemoveMember(member.id, member.user.name, false)}
                        disabled={isBusy}
                        className="ml-auto text-xs text-red-400 hover:text-red-600 disabled:opacity-50 transition-colors"
                      >
                        {isBusy ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  )}

                  {isMe && !isOwner && (
                    <div className="mt-2 pl-12">
                      <button
                        onClick={() => handleRemoveMember(member.id, member.user.name, true)}
                        disabled={isBusy}
                        className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50 transition-colors"
                      >
                        {isBusy ? 'Leaving…' : 'Leave family'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredMembers.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                {search ? 'No members match your search.' : 'No members yet.'}
              </div>
            )}
          </div>
        )}

        {isOwner && !showInvite && (
          <button
            onClick={() => setShowInvite(true)}
            className="mt-3 w-full py-2.5 rounded-xl border border-dashed border-gray-300 text-sm text-gray-500 hover:text-primary-700 hover:border-primary-300 hover:bg-primary-50/30 transition-colors"
          >
            + Invite family member
          </button>
        )}
        {showInvite && (
          <div className="mt-3 p-5 rounded-xl border border-gray-200 bg-white">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Invite family member</h3>
            <CreateInviteForm
              familyId={familyId}
              onSuccess={() => { setShowInvite(false); reload(); }}
              onCancel={() => setShowInvite(false)}
            />
          </div>
        )}
      </section>

      {/* ── Carer load ──────────────────────────────────────────────────────── */}
      {carerLoad.length > 0 && carerLoad.some((m) => m.total > 0) && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Carer load</h2>
          <p className="text-sm text-gray-500 mb-4">Who's carrying what this month</p>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {(() => {
              const maxTotal = Math.max(...carerLoad.map((x) => x.total), 1);
              return carerLoad.map((m) => {
                const pct = Math.round((m.total / maxTotal) * 100);
                const initials = m.name
                  .split(' ')
                  .map((p: string) => p[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2);
                const breakdown = [
                  m.tasksAssigned > 0 && `${m.tasksAssigned} task${m.tasksAssigned !== 1 ? 's' : ''}`,
                  m.appointmentsAttending > 0 && `${m.appointmentsAttending} appointment${m.appointmentsAttending !== 1 ? 's' : ''}`,
                  m.activitiesAttending > 0 && `${m.activitiesAttending} activit${m.activitiesAttending !== 1 ? 'ies' : 'y'}`,
                ].filter(Boolean).join(' · ') || 'No activity yet';
                return (
                  <div key={m.userId} className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="font-medium text-gray-900 truncate">{m.name}</span>
                        <span className="text-xs text-gray-400 shrink-0 ml-2">{m.total} event{m.total !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-1.5 bg-primary-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{breakdown}</p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </section>
      )}
    </div>
  );
}
